import { doseFromFileItem } from './doseFromHeaders'
import { parseRdsrFromFileItem } from './rdsrParse'
import { isRdsrFileItem, findRdsrSeriesInStore } from './isRdsrSeries'
import { isScoutFileItem } from '../isScoutSeries'
import { evaluateDrlLevel, resolveDrlProfile } from '../../config/drlThresholds'

/**
 * @param {{ seriesList?: Map, seriesKeys?: any[] }|null} seriesStore
 * @param {object[]} [activeFiles]
 * @returns {{
 *   seriesRows: object[],
 *   totals: { ctdivol: number|null, dlp: number|null, dap: number|null },
 *   source: string,
 *   drlLevel: string,
 *   profileLabel: string,
 *   modality: string,
 *   description: string,
 * }|null}
 */
export function aggregateStudyDose(seriesStore, activeFiles) {
  const rows = []
  let totalCtdi = null
  let totalDlp = null
  let totalDap = null
  let source = 'none'
  let modality = ''
  let description = ''

  // Preferir RDSR si existe
  const rdsr = findRdsrSeriesInStore(seriesStore)
  if (rdsr) {
    const file = rdsr.files.find((f) => f && f.image) || rdsr.files[0]
    const parsed = parseRdsrFromFileItem(file)
    if (parsed && !parsed.empty) {
      source = 'rdsr'
      if (parsed.ctdivol != null) totalCtdi = parsed.ctdivol
      if (parsed.dlp != null) totalDlp = parsed.dlp
      if (parsed.dap != null) totalDap = parsed.dap
      rows.push({
        seriesNumber: file && file.series && file.series.seriesNumber,
        seriesDescription:
          (file && file.series && file.series.seriesDescription) || 'RDSR',
        modality: 'SR',
        kvp: null,
        ma: null,
        mas: null,
        ctdivol: parsed.ctdivol,
        dlp: parsed.dlp,
        dap: parsed.dap,
        source: 'rdsr',
      })
    }
  }

  const keys =
    seriesStore && seriesStore.seriesList
      ? Array.isArray(seriesStore.seriesKeys) && seriesStore.seriesKeys.length
        ? seriesStore.seriesKeys
        : [...seriesStore.seriesList.keys()]
      : []

  for (const key of keys) {
    const files = seriesStore.seriesList.get(key)
    if (!Array.isArray(files) || !files.length) continue
    const sample = files.find((f) => f && f.image) || files[0]
    if (!sample) continue
    if (isRdsrFileItem(sample) || isScoutFileItem(sample)) continue

    const mod = String((sample.series && sample.series.modality) || '').toUpperCase()
    if (!['CT', 'CR', 'DX', 'RF', 'XA', 'RG', 'MG'].includes(mod)) continue

    const metrics = doseFromFileItem(sample)
    if (
      metrics.ctdivol == null &&
      metrics.dlp == null &&
      metrics.dap == null &&
      metrics.kvp == null
    ) {
      continue
    }

    if (!modality) modality = mod
    if (!description) {
      description =
        (sample.study && sample.study.studyDescription) ||
        metrics.seriesDescription ||
        ''
    }

    rows.push({
      seriesNumber: metrics.seriesNumber,
      seriesDescription: metrics.seriesDescription,
      modality: metrics.modality,
      kvp: metrics.kvp,
      ma: metrics.ma,
      mas: metrics.mas,
      ctdivol: metrics.ctdivol,
      dlp: metrics.dlp,
      dap: metrics.dap,
      source: 'headers',
    })

    if (source !== 'rdsr') {
      source = 'headers'
      if (metrics.ctdivol != null) {
        totalCtdi = (totalCtdi || 0) + metrics.ctdivol
      }
      if (metrics.dap != null) {
        totalDap = (totalDap || 0) + metrics.dap
      }
    }
  }

  // Fallback: archivos activos si no hay series store
  if (!rows.length && Array.isArray(activeFiles) && activeFiles.length) {
    const sample = activeFiles.find((f) => f && f.image) || activeFiles[0]
    if (sample && !isScoutFileItem(sample) && !isRdsrFileItem(sample)) {
      const metrics = doseFromFileItem(sample)
      modality = metrics.modality || modality
      description =
        (sample.study && sample.study.studyDescription) ||
        metrics.seriesDescription ||
        ''
      if (metrics.kvp != null || metrics.ctdivol != null || metrics.dap != null) {
        rows.push({
          seriesNumber: metrics.seriesNumber,
          seriesDescription: metrics.seriesDescription,
          modality: metrics.modality,
          kvp: metrics.kvp,
          ma: metrics.ma,
          mas: metrics.mas,
          ctdivol: metrics.ctdivol,
          dlp: metrics.dlp,
          dap: metrics.dap,
          source: 'headers',
        })
        source = 'headers'
        totalCtdi = metrics.ctdivol
        totalDap = metrics.dap
      }
    }
  }

  if (!rows.length && totalDlp == null && totalCtdi == null && totalDap == null) {
    return null
  }

  const profile = resolveDrlProfile(description, modality || 'CT')
  const totals = { ctdivol: totalCtdi, dlp: totalDlp, dap: totalDap }
  const drlLevel = evaluateDrlLevel({
    dlp: totalDlp,
    dap: totalDap,
    modality: modality || 'CT',
    description,
  })

  return {
    seriesRows: rows,
    totals,
    source,
    drlLevel,
    profileLabel: profile.label,
    modality: modality || '',
    description,
  }
}
